import { useMemo } from 'react';
import styled from 'styled-components';

import { useExtensionPresence } from 'hooks/useExtensionPresence';
import PortalConnection from 'models/PortalConnection';
import { Body, BodySmall } from 'styles/typography';

import TaskCard from './TaskCard';
import {
  filterPatientCards,
  OperationalTaskCard,
  orderTaskCards,
  PatientTaskCard,
  TaskFilters,
  TaskSort,
} from './taskCards';

interface Props {
  // Built by the page: the tab count is over the UNFILTERED set (operational cards included),
  // so the page has to hold the cards anyway and building them twice would let the count and
  // the list disagree.
  patientCards: PatientTaskCard[];
  operational: OperationalTaskCard[];
  isLoading: boolean;
  isError: boolean;
  connections: PortalConnection[];
  portalName: (sourceSystem?: string | null) => string;
  // Sort and filters are the page's: their controls (the FilterBar and the toolbar band) render
  // outside this tab. This receives only the applied values, plus a reset for the empty state.
  sort: TaskSort;
  filters: TaskFilters;
  onClearFilters: () => void;
  // The open gesture carries the traversal order with it: the filtered, sorted collection lives
  // here, and the slideout's Prev/Next walk THAT list rather than a global one. Passing it at
  // click time (instead of publishing it upward on every render) is what keeps the order the
  // reader was looking at from being replaced by a later refetch's.
  onOpenTask: (workflowId: string, orderedWorkflowIds: string[]) => void;
  onManagePortals: (sourceSystem?: string) => void;
}

// `Open in <portal>` exists in M1 and M2 — the extension opens the portal tab. Headless has
// nothing for a person to open, and without the extension there is nothing to open it with.
const LAUNCHABLE_MODES = ['manual', 'extension'];

export default function TasksTab({
  patientCards,
  operational,
  isLoading,
  isError,
  connections,
  portalName,
  sort,
  filters,
  onClearFilters,
  onOpenTask,
  onManagePortals,
}: Props) {
  const { present } = useExtensionPresence();

  const cards = useMemo(
    () => orderTaskCards(operational, filterPatientCards(patientCards, filters), sort),
    [operational, patientCards, filters, sort]
  );

  // Patient cards only: the operational cards pinned above them are portal-level and belong to
  // no task, so an arrow must never land on one.
  const patientOrder = cards.flatMap((card) => (card.kind === 'patient' ? [card.workflowId] : []));

  if (isError) return <Message data-testid='tasks-error'>We couldn’t load your tasks.</Message>;

  const filtered = filters.sourceSystems.length > 0 || filters.reasons.length > 0;

  return (
    <Wrapper>
      {isLoading ? (
        <Message data-testid='tasks-loading'>Loading tasks…</Message>
      ) : cards.length === 0 ? (
        <EmptyState filtered={filtered} connections={connections} onClearFilters={onClearFilters} />
      ) : (
        <Cards data-testid='task-cards'>
          {cards.map((card) => (
            <TaskCard
              key={card.id}
              card={card}
              portalName={portalName(card.sourceSystem)}
              canOpenInPortal={
                card.kind === 'patient' && present === true && LAUNCHABLE_MODES.includes(card.retrievalMode)
              }
              detectingExtension={
                card.kind === 'patient' && present === null && LAUNCHABLE_MODES.includes(card.retrievalMode)
              }
              onOpen={(workflowId) => onOpenTask(workflowId, patientOrder)}
              onManagePortals={onManagePortals}
            />
          ))}
        </Cards>
      )}
    </Wrapper>
  );
}

// None of these are drawn. Three cases, and they are genuinely different sentences: a filter
// that matched nothing, an all-clear on a portal that scans itself, and a client doing this by
// hand — which is also the case where the tab bar is degenerate, so the empty state is the only
// thing on the page explaining what it is for.
function EmptyState({
  filtered,
  connections,
  onClearFilters,
}: {
  filtered: boolean;
  connections: PortalConnection[];
  onClearFilters: () => void;
}) {
  if (filtered) {
    return (
      <Message data-testid='tasks-empty-filtered'>
        <Body>No tasks match these filters.</Body>
        <ClearLink type='button' onClick={onClearFilters}>
          Clear filters
        </ClearLink>
      </Message>
    );
  }

  if (connections.some((connection) => connection.canScan)) {
    return (
      <Message data-testid='tasks-empty-scannable'>
        <Body>You’re all caught up.</Body>
        <BodySmall>New tasks appear here as portal scans find referrals that need you.</BodySmall>
      </Message>
    );
  }

  return (
    <Message data-testid='tasks-empty'>
      <Body>No tasks right now.</Body>
      <BodySmall>A task appears here when a referral from a portal needs your attention.</BodySmall>
    </Message>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Cards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Message = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 48px 0;
  text-align: center;
  color: ${({ theme }) => theme.colors.black75};
`;

const ClearLink = styled.button`
  font-family: ${({ theme }) => theme.textStyles.base.fontFamily};
  font-size: 14px;
  color: ${({ theme }) => theme.colors.blue100};
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
`;
